import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { compressImage, base64ToBlob } from "@/lib/imageUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Trash2, ArrowLeft, Upload } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

interface Hairstyle {
  id: string;
  name: string;
  image_url: string;
  category: string | null;
  sex: string | null;
  ethnicity: string | null;
}

interface FacialHair {
  id: string;
  name: string;
  image_url: string;
  category: string | null;
  sex: string | null;
  ethnicity: string | null;
}

const Admin = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [hairstyles, setHairstyles] = useState<Hairstyle[]>([]);
  const [facialHairs, setFacialHairs] = useState<FacialHair[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingFacialHairs, setGeneratingFacialHairs] = useState(false);
  const [generatingSouthAsian, setGeneratingSouthAsian] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteTable, setDeleteTable] = useState<'hairstyles' | 'facial_hair'>('hairstyles');
  const [deleteGroupIds, setDeleteGroupIds] = useState<string[] | null>(null);
  const [deleteGroupTable, setDeleteGroupTable] = useState<'hairstyles' | 'facial_hair'>('hairstyles');
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [useGeneration, setUseGeneration] = useState(false);
  const [newHairstyle, setNewHairstyle] = useState({
    name: "",
    category: "",
    sex: "male",
    ethnicity: "caucasian",
    file: null as File | null,
    generatedImage: null as string | null
  });
  const [newFacialHair, setNewFacialHair] = useState({
    name: "",
    category: "beard",
    sex: "male",
    ethnicity: "caucasian",
    file: null as File | null,
  });

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/");
      toast({
        title: "Access denied",
        description: "You don't have permission to access this page.",
        variant: "destructive",
      });
    }
  }, [isAdmin, authLoading, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchHairstyles();
      fetchFacialHairs();
    }
  }, [isAdmin]);

  const fetchHairstyles = async () => {
    try {
      const { data, error } = await supabase
        .from('hairstyles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setHairstyles(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFacialHairs = async () => {
    try {
      const { data, error } = await supabase
        .from('facial_hair')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFacialHairs(data || []);
    } catch (error: any) {
      toast({
        title: "Error fetching facial hair",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setNewHairstyle({ ...newHairstyle, file: e.target.files[0] });
    }
  };

  const handleGenerateImage = async () => {
    if (!newHairstyle.name || !newHairstyle.category || !newHairstyle.sex) {
      toast({
        title: "Error",
        description: "Please fill in name, category, and sex before generating",
        variant: "destructive",
      });
      return;
    }

    setGenerating(true);
    const ethnicities = ['caucasian', 'african', 'asian', 'hispanic', 'middle_eastern'];
    
    try {
      toast({
        title: "Generating Images",
        description: `Creating ${ethnicities.length} ethnicity variants...`,
      });

      for (const ethnicity of ethnicities) {
        // Generate image for this ethnicity
        const { data, error } = await supabase.functions.invoke('generate-hairstyle-image', {
          body: {
            name: newHairstyle.name,
            category: newHairstyle.category,
            sex: newHairstyle.sex,
            ethnicity: ethnicity,
          },
        });

        if (error) throw error;
        if (!data?.image) throw new Error(`No image returned for ${ethnicity}`);

        // Compress to blob directly
        const blob = await compressImage(data.image, 1024, 1024, 0.85);
        
        // Upload to storage
        const fileName = `${newHairstyle.name.toLowerCase().replace(/\s+/g, '-')}-${ethnicity}-${Math.random().toString(36).substring(7)}.jpg`;
        const { error: uploadError } = await supabase.storage
          .from('hairstyle-images')
          .upload(fileName, blob);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('hairstyle-images')
          .getPublicUrl(fileName);

        // Save to database
        const { error: dbError } = await supabase
          .from('hairstyles')
          .insert({
            name: newHairstyle.name,
            image_url: publicUrl,
            category: newHairstyle.category || null,
            sex: newHairstyle.sex,
            ethnicity: ethnicity
          });

        if (dbError) throw dbError;

        toast({
          title: "Progress",
          description: `Saved ${ethnicity} variant`,
        });
      }

      // Reset form and refresh list
      setNewHairstyle({ 
        name: "", 
        category: "", 
        sex: "male",
        ethnicity: "caucasian",
        file: null,
        generatedImage: null
      });
      setUseGeneration(false);
      await fetchHairstyles();

      toast({
        title: "Complete!",
        description: `Successfully generated ${ethnicities.length} ethnicity variants!`,
      });
    } catch (error: any) {
      console.error('Error generating images:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate images",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleAddHairstyle = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newHairstyle.name || !newHairstyle.category || !newHairstyle.sex || !newHairstyle.ethnicity) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (!useGeneration && !newHairstyle.file) {
      toast({
        title: "Error",
        description: "Please select an image or generate one",
        variant: "destructive",
      });
      return;
    }

    if (useGeneration && !newHairstyle.generatedImage) {
      toast({
        title: "Error",
        description: "Please generate an image first",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      let publicUrl = '';

      if (useGeneration && newHairstyle.generatedImage) {
        // Compress to blob directly
        const blob = await compressImage(newHairstyle.generatedImage, 1024, 1024, 0.85);
        
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.jpg`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('hairstyle-images')
          .upload(filePath, blob);

        if (uploadError) throw uploadError;

        const { data: { publicUrl: url } } = supabase.storage
          .from('hairstyle-images')
          .getPublicUrl(filePath);
        
        publicUrl = url;
      } else {
        const fileExt = newHairstyle.file!.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('hairstyle-images')
          .upload(filePath, newHairstyle.file!);

        if (uploadError) throw uploadError;

        const { data: { publicUrl: url } } = supabase.storage
          .from('hairstyle-images')
          .getPublicUrl(filePath);
        
        publicUrl = url;
      }

      const { error: dbError } = await supabase
        .from('hairstyles')
        .insert({
          name: newHairstyle.name,
          image_url: publicUrl,
          category: newHairstyle.category || null,
          sex: newHairstyle.sex,
          ethnicity: newHairstyle.ethnicity
        });

      if (dbError) throw dbError;

      setNewHairstyle({ 
        name: "", 
        category: "", 
        sex: "male",
        ethnicity: "caucasian",
        file: null,
        generatedImage: null
      });
      setUseGeneration(false);
      fetchHairstyles();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from(deleteTable)
        .delete()
        .eq('id', deleteId);

      if (error) throw error;

      if (deleteTable === 'hairstyles') {
        fetchHairstyles();
      } else {
        fetchFacialHairs();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupIds || deleteGroupIds.length === 0) return;

    try {
      const { error } = await supabase
        .from(deleteGroupTable)
        .delete()
        .in('id', deleteGroupIds);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Deleted ${deleteGroupIds.length} variants`,
      });

      if (deleteGroupTable === 'hairstyles') {
        fetchHairstyles();
      } else {
        fetchFacialHairs();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteGroupIds(null);
    }
  };

  const handleRegenerateVariant = async (style: Hairstyle) => {
    if (!style.name || !style.category || !style.sex || !style.ethnicity) {
      toast({
        title: "Error",
        description: "Missing required fields for regeneration",
        variant: "destructive",
      });
      return;
    }

    setRegeneratingId(style.id);
    
    try {
      toast({
        title: "Regenerating",
        description: `Creating new ${style.ethnicity} variant...`,
      });

      // Generate new image
      const { data, error } = await supabase.functions.invoke('generate-hairstyle-image', {
        body: {
          name: style.name,
          category: style.category,
          sex: style.sex,
          ethnicity: style.ethnicity,
        },
      });

      if (error) throw error;
      if (!data?.image) throw new Error('No image returned');

      // Compress to blob directly
      const blob = await compressImage(data.image, 1024, 1024, 0.85);
      
      // Upload to storage
      const fileName = `${style.name.toLowerCase().replace(/\s+/g, '-')}-${style.ethnicity}-${Math.random().toString(36).substring(7)}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('hairstyle-images')
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('hairstyle-images')
        .getPublicUrl(fileName);

      // Update database
      const { error: updateError } = await supabase
        .from('hairstyles')
        .update({ image_url: publicUrl })
        .eq('id', style.id);

      if (updateError) throw updateError;

      await fetchHairstyles();

      toast({
        title: "Success",
        description: `Regenerated ${style.ethnicity} variant!`,
      });
    } catch (error: any) {
      console.error('Error regenerating variant:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to regenerate image",
        variant: "destructive",
      });
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleGenerateSouthAsian = async () => {
    setGeneratingSouthAsian(true);
    
    try {
      let offset = 0;
      let totalProcessed = 0;
      let totalRemaining = 0;
      
      // Process in batches until all are done
      do {
        toast({
          title: "Generating",
          description: `Processing batch ${Math.floor(offset / 10) + 1}... (${totalProcessed} completed)`,
        });

        const { data: functionData, error: functionError } = await supabase.functions.invoke(
          'generate-ethnicity-variants',
          {
            body: {
              ethnicity: 'south_asian',
              limit: 10,
              offset: offset
            }
          }
        );

        if (functionError) throw functionError;

        // Update progress
        totalProcessed = functionData?.progress?.processed || 0;
        totalRemaining = functionData?.progress?.remaining || 0;
        offset = functionData?.progress?.nextOffset || 0;
        
        // Count results
        const successful = functionData?.results?.filter((r: any) => r.status === 'success').length || 0;
        const skipped = functionData?.results?.filter((r: any) => r.status === 'skipped').length || 0;
        const errors = functionData?.results?.filter((r: any) => r.status === 'error').length || 0;
        
        console.log(`Batch complete: ${successful} successful, ${skipped} skipped, ${errors} errors`);
        
        // If we have errors about credits, stop immediately
        const creditError = functionData?.results?.find((r: any) => r.error?.includes('credits') || r.error?.includes('402'));
        if (creditError) {
          toast({
            title: "Error - Insufficient Credits",
            description: "Not enough credits. Please add credits and try again.",
            variant: "destructive",
          });
          break;
        }
        
      } while (totalRemaining > 0 && offset > 0);

      // Final summary
      toast({
        title: "Complete",
        description: `Processed all hairstyles. Total: ${totalProcessed}`,
      });

      fetchHairstyles();
    } catch (error: any) {
      console.error('Generation error:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to generate variants',
        variant: "destructive",
      });
    } finally {
      setGeneratingSouthAsian(false);
    }
  };

  const handleBatchGenerateFacialHair = async () => {
    const allFacialHairStyles = [
      { name: "Full Beard", category: "full", sex: "male" },
      { name: "Goatee", category: "goatee", sex: "male" },
      { name: "Mustache", category: "mustache", sex: "male" },
      { name: "Van Dyke", category: "goatee", sex: "male" },
      { name: "Stubble", category: "beard", sex: "male" },
      { name: "Soul Patch", category: "goatee", sex: "male" },
      { name: "Handlebar Mustache", category: "mustache", sex: "male" },
      { name: "Chevron Mustache", category: "mustache", sex: "male" },
    ];

    setGeneratingFacialHairs(true);
    
    try {
      // Process in smaller batches to avoid CPU timeout (1 style = 6 images per batch)
      const batchSize = 1;
      let totalSuccessful = 0;
      let totalErrors = 0;
      
      for (let i = 0; i < allFacialHairStyles.length; i += batchSize) {
        const batch = allFacialHairStyles.slice(i, i + batchSize);
        const batchNumber = Math.floor(i / batchSize) + 1;
        const totalBatches = Math.ceil(allFacialHairStyles.length / batchSize);
        
        toast({
          title: `Processing Batch ${batchNumber}/${totalBatches}`,
          description: `Generating ${batch.map(s => s.name).join(', ')}...`,
        });

        const { data, error } = await supabase.functions.invoke('batch-generate-facial-hair', {
          body: { facialHairs: batch }
        });

        if (error) {
          console.error(`Batch ${batchNumber} error:`, error);
          toast({
            title: `Batch ${batchNumber} Failed`,
            description: error.message || 'Failed to generate this batch',
            variant: "destructive",
          });
          continue; // Continue with next batch even if one fails
        }

        const successful = data?.results?.filter((r: any) => r.status === 'success').length || 0;
        const errors = data?.results?.filter((r: any) => r.status === 'error').length || 0;
        
        totalSuccessful += successful;
        totalErrors += errors;

        toast({
          title: `Batch ${batchNumber}/${totalBatches} Complete`,
          description: `Generated ${successful} variants. ${errors} errors.`,
        });
      }

      toast({
        title: "All Batches Complete!",
        description: `Total: ${totalSuccessful} successful, ${totalErrors} errors`,
      });

      fetchFacialHairs();
    } catch (error: any) {
      console.error('Batch generation error:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to batch generate facial hair',
        variant: "destructive",
      });
    } finally {
      setGeneratingFacialHairs(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  // Group hairstyles by name, category, and sex
  const groupedHairstyles = hairstyles.reduce((acc, style) => {
    const key = `${style.name}|${style.category}|${style.sex}`;
    if (!acc[key]) {
      acc[key] = {
        name: style.name,
        category: style.category,
        sex: style.sex,
        variants: []
      };
    }
    acc[key].variants.push(style);
    return acc;
  }, {} as Record<string, { name: string; category: string | null; sex: string | null; variants: Hairstyle[] }>);

  const groupedArray = Object.values(groupedHairstyles);

  // Group facial hair by name, category, and sex
  const groupedFacialHairs = facialHairs.reduce((acc, style) => {
    const key = `${style.name}|${style.category}|${style.sex}`;
    if (!acc[key]) {
      acc[key] = {
        name: style.name,
        category: style.category,
        sex: style.sex,
        variants: []
      };
    }
    acc[key].variants.push(style);
    return acc;
  }, {} as Record<string, { name: string; category: string | null; sex: string | null; variants: FacialHair[] }>);

  const groupedFacialHairArray = Object.values(groupedFacialHairs);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Admin Panel
          </h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/admin/analytics")}>
              Analytics
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/migrate-data")}>
              Delete Old Data
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/batch-generate")}>
              Batch Generate
            </Button>
          </div>
        </div>

        <Tabs defaultValue="hairstyles" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="hairstyles">Hairstyles ({hairstyles.length})</TabsTrigger>
            <TabsTrigger value="facial-hair">Facial Hair ({facialHairs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="hairstyles" className="space-y-6">
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={handleGenerateSouthAsian}
                disabled={generatingSouthAsian}
              >
                {generatingSouthAsian ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Generate South Asian"
                )}
              </Button>
            </div>

        {/* Add New Hairstyle */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Plus className="mr-2 h-5 w-5" />
              Add New Hairstyle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddHairstyle} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={newHairstyle.name}
                    onChange={(e) => setNewHairstyle({ ...newHairstyle, name: e.target.value })}
                    placeholder="e.g., Low Fade, High Fade"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Input
                    id="category"
                    value={newHairstyle.category}
                    onChange={(e) => setNewHairstyle({ ...newHairstyle, category: e.target.value })}
                    placeholder="e.g., Fade, Crop, Buzz Cut"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sex">Sex *</Label>
                <select
                  id="sex"
                  value={newHairstyle.sex}
                  onChange={(e) => setNewHairstyle({ ...newHairstyle, sex: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  required
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="unisex">Unisex</option>
                </select>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  id="useGeneration"
                  checked={useGeneration}
                  onChange={(e) => {
                    setUseGeneration(e.target.checked);
                    if (e.target.checked) {
                      setNewHairstyle(prev => ({ ...prev, file: null }));
                    } else {
                      setNewHairstyle(prev => ({ ...prev, generatedImage: null }));
                    }
                  }}
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="useGeneration" className="cursor-pointer font-normal">
                  Generate all 5 ethnicity variants with AI (caucasian, african, asian, hispanic, middle eastern)
                </Label>
              </div>

              {useGeneration ? (
                <div className="space-y-4">
                  <Button
                    type="button"
                    onClick={handleGenerateImage}
                    disabled={generating}
                    className="w-full"
                    variant="secondary"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating & Saving All Variants...
                      </>
                    ) : (
                      "Generate & Save All 5 Ethnicity Variants"
                    )}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    This will automatically generate 5 versions (one for each ethnicity) and save them directly to the database.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="ethnicity">Ethnicity *</Label>
                    <select
                      id="ethnicity"
                      value={newHairstyle.ethnicity}
                      onChange={(e) => setNewHairstyle({ ...newHairstyle, ethnicity: e.target.value })}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      required
                    >
                      <option value="caucasian">Caucasian</option>
                      <option value="african">African</option>
                      <option value="asian">Asian</option>
                      <option value="hispanic">Hispanic</option>
                      <option value="middle_eastern">Middle Eastern</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="image">Upload Image *</Label>
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      required={!useGeneration}
                    />
                  </div>
                </>
              )}

              <Button type="submit" disabled={uploading || generating}>
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Add Hairstyle
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

            {/* Existing Hairstyles */}
            <Card>
              <CardHeader>
                <CardTitle>Manage Hairstyles ({groupedArray.length} styles, {hairstyles.length} total variants)</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="w-full space-y-2">
                  {groupedArray.map((group, idx) => (
                    <AccordionItem key={idx} value={`item-${idx}`} className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-4">
                          <div className="text-left flex-1">
                            <h3 className="font-semibold text-lg">{group.name}</h3>
                            <div className="text-sm text-muted-foreground">
                              {group.category && <span>Category: {group.category}</span>}
                              {group.sex && <span> • Sex: {group.sex}</span>}
                              <span> • {group.variants.length} variants</span>
                            </div>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteGroupIds(group.variants.map(v => v.id));
                              setDeleteGroupTable('hairstyles');
                            }}
                            className="ml-4"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Style
                          </Button>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 pt-2">
                          {group.variants.map((style) => (
                            <Card key={style.id} className="overflow-hidden">
                              <div className="aspect-square relative">
                                <img
                                  src={style.image_url}
                                  alt={style.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <CardContent className="p-2 space-y-1">
                                <p className="text-xs font-medium capitalize">{style.ethnicity}</p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full"
                                  onClick={() => handleRegenerateVariant(style)}
                                  disabled={regeneratingId === style.id}
                                >
                                  {regeneratingId === style.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    "Regenerate"
                                  )}
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="w-full"
                                  onClick={() => {
                                    setDeleteId(style.id);
                                    setDeleteTable('hairstyles');
                                  }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="facial-hair" className="space-y-6">
            <div className="flex gap-2 justify-end">
              <Button 
                variant="default" 
                onClick={handleBatchGenerateFacialHair}
                disabled={generatingFacialHairs}
              >
                {generatingFacialHairs ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Batch Generate 8 Styles"
                )}
              </Button>
            </div>

            {/* Manage Facial Hair */}
            <Card>
              <CardHeader>
                <CardTitle>Manage Facial Hair ({groupedFacialHairArray.length} styles, {facialHairs.length} total variants)</CardTitle>
              </CardHeader>
              <CardContent>
                {groupedFacialHairArray.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No facial hair styles yet. Click "Batch Generate 8 Styles" to create some!</p>
                  </div>
                ) : (
                  <Accordion type="multiple" className="w-full space-y-2">
                    {groupedFacialHairArray.map((group, idx) => (
                      <AccordionItem key={idx} value={`facial-${idx}`} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center justify-between w-full pr-4">
                            <div className="text-left flex-1">
                              <h3 className="font-semibold text-lg">{group.name}</h3>
                              <div className="text-sm text-muted-foreground">
                                {group.category && <span className="capitalize">Category: {group.category}</span>}
                                {group.sex && <span> • Sex: {group.sex}</span>}
                                <span> • {group.variants.length} variants</span>
                              </div>
                            </div>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteGroupIds(group.variants.map(v => v.id));
                                setDeleteGroupTable('facial_hair');
                              }}
                              className="ml-4"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Style
                            </Button>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 pt-2">
                            {group.variants.map((style) => (
                              <Card key={style.id} className="overflow-hidden">
                                <div className="aspect-square relative">
                                  <img
                                    src={style.image_url}
                                    alt={style.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <CardContent className="p-2 space-y-1">
                                  <p className="text-xs font-medium capitalize">{style.ethnicity}</p>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    className="w-full"
                                    onClick={() => {
                                      setDeleteId(style.id);
                                      setDeleteTable('facial_hair');
                                    }}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the hairstyle variant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteGroupIds} onOpenChange={() => setDeleteGroupIds(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entire Style?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete all {deleteGroupIds?.length || 0} ethnicity variants of this hairstyle.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteGroup} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete All Variants
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Admin;
